import { useRef, useState } from "react";
import { Image as ImageIcon, Upload, RotateCcw, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { setBackground, clearBackground, uploadBackground, absUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Preset backgrounds, served from this site (frontend/public/img/bg) so no
// third party sees who opens the app and the images can't vanish upstream.
export const BG_PRESETS = [1, 2, 3, 4, 5].map((n) => `/img/bg/bg-${n}.jpg`);

export const DEFAULT_BG = BG_PRESETS[0];

// Accounts that picked a preset before the images were self-hosted still have
// the old Unsplash address saved. Show them the same picture from here.
const LEGACY_PRESETS = {
    "photo-1519681393784-d120267933ba": BG_PRESETS[0],
    "photo-1470252649378-9c29740c9fa8": BG_PRESETS[1],
    "photo-1499346030926-9a72daac6c63": BG_PRESETS[2],
    "photo-1500051638674-ff996a0ec29e": BG_PRESETS[3],
    "photo-1490750967868-88aa4486c946": BG_PRESETS[4],
};

export function resolveBgUrl(user) {
    const raw = user?.bg_image_url;
    if (!raw) return DEFAULT_BG;
    const legacy = raw.match(/^https:\/\/images\.unsplash\.com\/(photo-[\w-]+)/);
    if (legacy) return LEGACY_PRESETS[legacy[1]] || DEFAULT_BG;
    if (raw.startsWith("/img/")) return raw; // a preset on this site, not the API
    if (raw.startsWith("http")) return raw;
    return absUrl(raw); // an uploaded background, served by the API
}

export default function BackgroundPicker({ open, onClose }) {
    const { user, refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const fileInputRef = useRef(null);

    const currentUrl = resolveBgUrl(user);

    async function pickPreset(url) {
        setBusy(true);
        try {
            await setBackground(url);
            await refresh();
            toast.success("Background updated");
            onClose?.();
        } catch (e) {
            toast.error("Couldn't update background");
        }
        setBusy(false);
    }

    async function onFileChosen(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            await uploadBackground(file);
            await refresh();
            toast.success("Background uploaded");
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        }
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    async function reset() {
        setBusy(true);
        try {
            await clearBackground();
            await refresh();
            toast.success("Background reset");
            onClose?.();
        } catch {
            toast.error("Couldn't reset");
        }
        setBusy(false);
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose?.()}>
            <DialogContent data-testid="background-picker-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl font-normal flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-primary" /> Change background
                    </DialogTitle>
                    <DialogDescription>
                        Pick a preset scene or upload your own. Your choice is saved to your account only.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                    {BG_PRESETS.map((url, idx) => {
                        const selected = currentUrl === url;
                        return (
                            <button
                                key={url}
                                type="button"
                                data-testid={`bg-preset-${idx}`}
                                onClick={() => pickPreset(url)}
                                disabled={busy}
                                className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${
                                    selected ? "border-primary ring-2 ring-primary/40" : "border-border/60 hover:border-border"
                                } ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                            >
                                <img src={url} alt={`Preset ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                                {selected && (
                                    <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md">
                                        <Check className="w-3.5 h-3.5" />
                                    </div>
                                )}
                                {idx === 0 && (
                                    <div className="absolute bottom-1 left-1 text-[10px] uppercase tracking-wider text-white bg-black/60 px-1.5 py-0.5 rounded">Default</div>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-5 flex flex-col sm:flex-row items-center gap-2 border-t pt-4">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onFileChosen}
                        className="hidden"
                        data-testid="bg-upload-input"
                    />
                    <Button
                        data-testid="bg-upload-button"
                        variant="outline"
                        className="w-full sm:w-auto rounded-full"
                        disabled={busy}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Upload your own
                    </Button>
                    <Button
                        data-testid="bg-reset-button"
                        variant="ghost"
                        className="w-full sm:w-auto rounded-full text-muted-foreground"
                        disabled={busy}
                        onClick={reset}
                    >
                        <RotateCcw className="w-4 h-4" /> Reset to default
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
